import React from "react";
import type { Step, ExclusionCondition, HubSpotProduct } from "../../types/configurator";
import { TrashIcon } from "../ui/Icons";

interface Props {
  exclusion: ExclusionCondition;
  condStep: Step;        // prior step whose selections trigger this exclusion
  condStepIndex: number;
  thisStep: Step;        // current step whose items can be excluded
  productMap: Record<string, HubSpotProduct>;
  onUpdate: (exclusion: ExclusionCondition) => void;
  onRemove: () => void;
}

function getItemId(item: { type: string; productId?: string; value?: string }): string {
  return item.type === 'product' ? (item.productId ?? '') : (item.value ?? '');
}

function getItemLabel(
  item: { type: string; productId?: string; value?: string; label?: string },
  productMap: Record<string, HubSpotProduct>,
): string | null {
  if (item.type === 'product') return productMap[item.productId ?? '']?.name ?? null;
  return (item as any).label ?? null;
}

export const ExclusionRow: React.FC<Props> = ({
  exclusion,
  condStep,
  condStepIndex,
  thisStep,
  productMap,
  onUpdate,
  onRemove,
}) => {
  const toggleTrigger = (value: string) => {
    const next = exclusion.selectedAny.includes(value)
      ? exclusion.selectedAny.filter((v) => v !== value)
      : [...exclusion.selectedAny, value];
    onUpdate({ ...exclusion, selectedAny: next });
  };

  const toggleExclude = (value: string) => {
    const next = exclusion.excludeItems.includes(value)
      ? exclusion.excludeItems.filter((v) => v !== value)
      : [...exclusion.excludeItems, value];
    onUpdate({ ...exclusion, excludeItems: next });
  };

  return (
    <div className="condition-row exclusion-row">
      <div className="condition-row-header exclusion-row-header">
        <span className="condition-step-label">
          Step {condStepIndex + 1}: {condStep.title || `Step ${condStepIndex + 1}`}
        </span>
        <button className="remove-condition" onClick={onRemove} title="Remove exclusion">
          <TrashIcon />
        </button>
      </div>

      <div className="condition-row-body">
        <span className="condition-hint">If any of these are selected:</span>
        <div className="condition-values">
          {condStep.items.map((item) => {
            const value = getItemId(item);
            const label = getItemLabel(item, productMap);
            if (!label) return null;
            return (
              <label key={item.uid} className="condition-value-label">
                <input
                  type="checkbox"
                  checked={exclusion.selectedAny.includes(value)}
                  onChange={() => toggleTrigger(value)}
                />
                {label}
              </label>
            );
          })}
        </div>
      </div>

      <div className="condition-row-body exclusion-targets-body">
        <span className="condition-hint">Exclude from this step:</span>
        <div className="condition-values">
          {thisStep.items.map((item) => {
            const value = getItemId(item);
            const label = getItemLabel(item, productMap);
            if (!label) return null;
            return (
              <label key={item.uid} className="condition-value-label">
                <input
                  type="checkbox"
                  checked={exclusion.excludeItems.includes(value)}
                  onChange={() => toggleExclude(value)}
                />
                {label}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};
